import React, { useState } from "react";
import { FolderTree, Plus } from "lucide-react";
import ConnectStorageModal from "./ConnectStorageModal";
import FsBrowser from "./FsBrowser";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "./ui/dialog";

// A "nas"/"s3"/"gcs"/"azure"-kind agent record is just stored connection
// details — nothing installs software on it, so browsing/transferring on
// its behalf requires a real installed agent (workerAgentId) to connect for it.
const CONNECTION_KINDS = new Set(["nas", "s3", "gcs", "azure"]);

/**
 * StorageEndpointPicker — source/destination picker used by both the
 * Data Migration wizard and the Backups (Schedules) wizard.
 *
 * Two ways to pick a connection:
 *   "Agent"     — choose from agents/connections already on the Devices page.
 *   "IP / Path" — connect inline to a NAS by IP or UNC path (reuses
 *                 ConnectStorageModal's Quick Connect, which already accepts
 *                 both smb://host/share and \\host\share). The new
 *                 connection record is created immediately and selected.
 *
 * Either way, once something is selected the path field gets a Browse
 * button that opens FsBrowser (drill into folders, pick one, or create a
 * new one) — this is how "navigate to a specific location" / "transfer a
 * specific folder" / "create a new folder" are satisfied for both source
 * and destination, and for both Data Migration and Backups.
 *
 * Props:
 *   agents          — full agent list (both real agents and connection records)
 *   onAgentsChange  — setter to append a newly inline-created connection record
 *   agentId / onAgentChange
 *   path / onPathChange
 *   workerAgentId   — the real agent chosen at the wizard level to act on
 *                      behalf of a connection-record endpoint (browsing and
 *                      the actual transfer both dispatch through it)
 */
export default function StorageEndpointPicker({
    label, testid, role,
    agents, onAgentsChange,
    agentId, onAgentChange,
    path, onPathChange,
    workerAgentId,
}) {
    const [mode, setMode] = useState("existing"); // "existing" | "connect"
    const [showConnect, setShowConnect] = useState(false);
    const [showBrowse, setShowBrowse] = useState(false);

    const selectedAgent = agents.find((a) => a.id === agentId);
    const isConnectionRecord = CONNECTION_KINDS.has(selectedAgent?.kind);
    const needsWorkerToBrowse = isConnectionRecord && !workerAgentId;
    const canBrowse = !!agentId && !needsWorkerToBrowse;

    return (
        <div className="space-y-2">
            <div className="flex items-center justify-between">
                <div className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground">{label}</div>
                <div className="inline-flex rounded border border-border bg-background p-0.5" role="tablist">
                    <button
                        type="button"
                        onClick={() => setMode("existing")}
                        data-testid={`${testid}-mode-agent`}
                        role="tab"
                        aria-selected={mode === "existing"}
                        className={[
                            "rounded px-2 py-0.5 text-[10px] font-medium",
                            mode === "existing" ? "bg-blue-500 text-white" : "text-muted-foreground hover:text-foreground",
                        ].join(" ")}
                    >
                        Agent
                    </button>
                    <button
                        type="button"
                        onClick={() => setMode("connect")}
                        data-testid={`${testid}-mode-connect`}
                        role="tab"
                        aria-selected={mode === "connect"}
                        className={[
                            "rounded px-2 py-0.5 text-[10px] font-medium",
                            mode === "connect" ? "bg-blue-500 text-white" : "text-muted-foreground hover:text-foreground",
                        ].join(" ")}
                    >
                        IP / Path
                    </button>
                </div>
            </div>

            {mode === "existing" ? (
                <select
                    value={agentId}
                    onChange={(e) => onAgentChange(e.target.value)}
                    data-testid={`${testid}-agent-select`}
                    className="w-full rounded border border-border bg-background px-3 py-2 text-sm"
                >
                    <option value="">Pick an agent…</option>
                    {agents.map((a) => (
                        <option key={a.id} value={a.id}>
                            {`${a.name || a.hostname || a.id.slice(0, 8)} (${a.kind || "vm"})`}
                        </option>
                    ))}
                </select>
            ) : (
                <div className="space-y-1.5">
                    <select
                        value={isConnectionRecord ? agentId : ""}
                        onChange={(e) => onAgentChange(e.target.value)}
                        data-testid={`${testid}-connection-select`}
                        className="w-full rounded border border-border bg-background px-3 py-2 text-sm"
                    >
                        <option value="">Pick a saved connection…</option>
                        {agents.filter((a) => CONNECTION_KINDS.has(a.kind)).map((a) => (
                            <option key={a.id} value={a.id}>{a.name || a.id.slice(0, 8)}</option>
                        ))}
                    </select>
                    <button
                        type="button"
                        onClick={() => setShowConnect(true)}
                        data-testid={`${testid}-connect-new-btn`}
                        className="inline-flex items-center gap-1 rounded border border-dashed border-blue-500/40 px-2 py-1 text-[11px] text-blue-300 hover:bg-blue-500/10"
                    >
                        <Plus size={11} /> Connect by IP or path…
                    </button>
                </div>
            )}

            <div className="flex gap-2">
                <input
                    value={path}
                    onChange={(e) => onPathChange(e.target.value)}
                    data-testid={`${testid}-path-input`}
                    placeholder="/mount/path"
                    required
                    className="flex-1 rounded border border-border bg-background px-3 py-2 font-mono text-xs"
                />
                <button
                    type="button"
                    onClick={() => setShowBrowse(true)}
                    disabled={!canBrowse}
                    data-testid={`${testid}-browse-btn`}
                    title={!agentId ? "Pick an agent or connection first" : needsWorkerToBrowse ? "Pick a worker agent first" : "Browse"}
                    className="shrink-0 rounded border border-border px-2.5 py-2 text-xs text-muted-foreground hover:border-foreground/40 hover:text-foreground disabled:opacity-30"
                >
                    <FolderTree size={13} />
                </button>
            </div>

            {showConnect && (
                <ConnectStorageModal
                    role={role}
                    onClose={() => setShowConnect(false)}
                    onSaved={(agent) => {
                        onAgentsChange((prev) => [...prev, agent]);
                        onAgentChange(agent.id);
                        setShowConnect(false);
                    }}
                />
            )}

            {showBrowse && (
                <Dialog open={true} onOpenChange={(o) => !o && setShowBrowse(false)}>
                    <DialogContent className="max-w-2xl" data-testid={`${testid}-browse-dialog`}>
                        <DialogHeader>
                            <DialogTitle className="flex items-center gap-2">
                                <FolderTree size={16} /> Browse {selectedAgent?.name || selectedAgent?.hostname || "storage"}
                            </DialogTitle>
                            <DialogDescription>
                                Click a folder name to open it, "Select" to use it as the {label.toLowerCase()} path, or the folder-plus icon to create a new one.
                            </DialogDescription>
                        </DialogHeader>
                        <FsBrowser
                            agentId={agentId}
                            kind={isConnectionRecord ? (selectedAgent.kind === "azure" ? "azure" : "nas") : "local"}
                            workerAgentId={isConnectionRecord ? workerAgentId : undefined}
                            initialPath={path}
                            onSelect={({ path: p }) => { onPathChange(p); setShowBrowse(false); }}
                        />
                    </DialogContent>
                </Dialog>
            )}
        </div>
    );
}
